import { useEffect, useRef, useState } from 'react';
import { ForqLoader } from '../bootstrap/ForqLoader';
import { lesePanelRect, vergissPanelRect } from './panelRect';
import type { PanelRect } from './panelRect';

/**
 * Der Übergang von der Anmeldung in den Arbeitsplatz.
 *
 * Drei Abschnitte, ohne künstliche Wartezeit dazwischen:
 *   1. WILLKOMMEN -- das rote Q, darunter zwei Zeilen. Rund 1,4 s; lädt FORQ länger, bleibt es
 *      stehen, bis der Arbeitsplatz wirklich steht.
 *   2. ÜBERGANG -- die Fläche des Anmeldefeldes zieht sich zusammen und wandert nach oben an die
 *      Stelle der globalen Suche. Dahinter blendet der Arbeitsplatz auf.
 *   3. Die Fläche verschwindet, und an ihrer Stelle steht die echte Suchleiste.
 *
 * Der Nutzer soll nicht von einer Seite auf eine andere springen, sondern sehen, wie sich FORQ
 * vor ihm öffnet.
 *
 * WARUM EIGENE FLÄCHE STATT DESSELBEN DOM-KNOTENS: Anmeldung und Arbeitsplatz liegen in
 * verschiedenen Zweigen des Bootstraps. Denselben Knoten über den Wechsel zu retten hieße, die
 * Verzweigung umzubauen. Stattdessen beginnt hier eine gleich aussehende Fläche an der gemerkten
 * Stelle -- der Inhalt des Anmeldefeldes ist zu diesem Zeitpunkt ohnehin schon ausgeblendet.
 */

/**
 * Der Zeitplan -- als reine Funktion, damit er prüfbar ist, ohne die Sequenz abzuspielen.
 *
 * `willkommenMs` ist eine UNTERgrenze, keine Wartezeit: dauert das Laden länger, bleibt das
 * Willkommen stehen, bis der Arbeitsplatz wirklich steht (der Übergang startet erst, wenn dieser
 * Baustein überhaupt eingehängt wird -- und das tut er erst im Zustand `ready`).
 */
export function accessSchedule(reduced: boolean): {
  readonly willkommenMs: number;
  readonly uebergangMs: number;
  readonly gesamtMs: number;
} {
  // Ohne Bewegung: kurz zeigen, was passiert, dann aufblenden. Kein Morph, kein Schweben.
  if (reduced) return { willkommenMs: 700, uebergangMs: 0, gesamtMs: 700 };
  return { willkommenMs: 1400, uebergangMs: 1400, gesamtMs: 2800 };
}

const { willkommenMs, uebergangMs } = accessSchedule(false);

type Abschnitt = 'willkommen' | 'uebergang';

interface AccessTransitionProps {
  /** Anrede im Willkommensmoment -- Vorname oder Anzeigename. */
  readonly name?: string;
  /**
   * Steht der Arbeitsplatz? Erst dann darf die Verwandlung beginnen -- vorher gibt es keine
   * Suchleiste, auf die sich die Fläche zubewegen könnte.
   *
   * Daraus folgt das gewünschte Verhalten ohne jede künstliche Wartezeit: lädt FORQ länger als
   * das Willkommen dauert, bleibt das Willkommen einfach stehen. Ist FORQ vorher fertig, gibt
   * die Mindestdauer den Takt.
   */
  readonly ready: boolean;
  /** Wird gerufen, wenn der Übergang vorbei ist und die Fläche verschwinden kann. */
  readonly onFinished: () => void;
}

function reduziert(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Die Stelle, an der die globale Suche sitzt -- das Ziel der Verwandlung. */
function suchleisteRect(): PanelRect | null {
  const ziel = document.querySelector('.forq-search');
  if (!ziel) return null;
  const rect = ziel.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

const alsCss = (rect: PanelRect) => ({
  left: `${rect.left}px`,
  top: `${rect.top}px`,
  width: `${rect.width}px`,
  height: `${rect.height}px`,
});

export function AccessTransition({ name, ready, onFinished }: AccessTransitionProps) {
  const [abschnitt, setAbschnitt] = useState<Abschnitt>('willkommen');
  const flaeche = useRef<HTMLDivElement>(null);
  const start = useRef<PanelRect | null>(lesePanelRect());

  // Der gemerkte Ausgangspunkt gilt nur für DIESEN Übergang. Bliebe er stehen, begänne ein
  // späterer Wechsel (Abmelden, zweite Anmeldung) an einer veralteten Stelle.
  useEffect(() => () => vergissPanelRect(), []);

  /**
   * Der Rückruf liegt in einer Ablage, damit der Ablauf NICHT von ihm abhängt.
   *
   * Sonst: der Aufrufer erzeugt bei jedem Rendern eine neue Funktion, der Effekt hängt daran,
   * räumt seine Uhren ab und beginnt von vorn -- die Sequenz startet sich selbst immer wieder neu
   * und kommt nie an. Genau das ist beim Ansehen aufgefallen.
   */
  const fertig = useRef(onFinished);
  useEffect(() => { fertig.current = onFinished; }, [onFinished]);

  /** Die Mindestdauer des Willkommens ist um. Sagt noch nichts darüber, ob FORQ bereit ist. */
  const [mindestzeitVorbei, setMindestzeitVorbei] = useState(false);

  useEffect(() => {
    if (reduziert()) {
      setMindestzeitVorbei(true);
      return;
    }
    const uhr = window.setTimeout(() => setMindestzeitVorbei(true), willkommenMs);
    return () => window.clearTimeout(uhr);
    // Läuft genau einmal -- der Rückruf steckt in der Ablage oben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Die Verwandlung beginnt, wenn BEIDES zutrifft: das Willkommen hat gewirkt UND der
  // Arbeitsplatz steht. Damit gibt es keine künstliche Wartezeit und kein Vorpreschen.
  useEffect(() => {
    if (abschnitt !== 'willkommen' || !mindestzeitVorbei || !ready) return;

    let abgebrochen = false;
    const uhren: number[] = [];
    const spaeter = (ms: number, tun: () => void) => {
      uhren.push(window.setTimeout(() => { if (!abgebrochen) tun(); }, ms));
    };

    if (reduziert()) {
      // Ohne Bewegung: kein Morph, nur kurz aufblenden lassen.
      setAbschnitt('uebergang');
      spaeter(260, () => fertig.current());
      return () => { abgebrochen = true; uhren.forEach(window.clearTimeout); };
    }

    setAbschnitt('uebergang');
    // Erst nach dem Bildaufbau messen: die Suchleiste ist da, sobald der Arbeitsplatz im Baum ist.
    requestAnimationFrame(() => {
      if (abgebrochen) return;
      const element = flaeche.current;
      const ziel = suchleisteRect();
      const von = start.current;

      if (element && ziel && von) {
        const breiteZwischenstation = Math.min(540, window.innerWidth - 38);
        const zwischenstation: PanelRect = {
          left: (window.innerWidth - breiteZwischenstation) / 2,
          top: von.top + von.height / 2 - 30,
          width: breiteZwischenstation,
          height: 60,
        };
        element.animate(
          [
            { ...alsCss(von), borderRadius: '18px', offset: 0 },
            { ...alsCss(zwischenstation), borderRadius: '12px', offset: 0.45 },
            { ...alsCss(ziel), borderRadius: '4px', offset: 1 },
          ],
          { duration: uebergangMs, fill: 'forwards', easing: 'cubic-bezier(.3,0,.18,1)' },
        );
      }

      spaeter(uebergangMs, () => fertig.current());
    });

    return () => { abgebrochen = true; uhren.forEach(window.clearTimeout); };
  }, [abschnitt, mindestzeitVorbei, ready]);

  const von = start.current;

  return (
    <div className={`forq-access-transition is-${abschnitt}`} role="status" aria-live="polite">
      <div className="forq-access-transition__veil" aria-hidden="true" />

      {von ? (
        <div
          ref={flaeche}
          className="forq-access-transition__surface"
          style={alsCss(von)}
          aria-hidden="true"
        />
      ) : null}

      <div className="forq-access-transition__welcome">
        <ForqLoader variant="pulse" size={78} label={null} />
        <p className="forq-access-transition__greeting">
          {name ? `Willkommen bei FORQ, ${name}` : 'Willkommen bei FORQ'}
        </p>
        <p className="forq-access-transition__hint">Dein Arbeitsplatz wird vorbereitet.</p>
      </div>
    </div>
  );
}
